/**
 * EventStreamDetail
 * =================
 *
 * Detail body for an EVENT STREAM row (used by SignalDetailModal instead of
 * the numeric SignalCard/chart). Events are first-class here:
 *
 *   - a stats strip: event count, scheduled (known-future) occurrences,
 *     and the discovery graph's verdict (which goals the stream drives,
 *     strongest lift, fitted response half-life);
 *   - the signed score sparkline over occurred time;
 *   - the actual event list, newest occurrence first, with LLM scores,
 *     numeric values, both clocks, and scheduled badges — pageable.
 *
 * Marketplace-subscribed streams render from the row's inlined points
 * (their raw events live in the seller's workspace, not ours).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert, Box, Chip, CircularProgress, Divider, Stack, Tooltip, Typography,
  alpha, useTheme,
} from '@mui/material';
import {
  Bolt as BoltIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
} from '@mui/icons-material';
import { CMButtonOutlined } from '../../containers/styledComponents';
import EventMiniSparkline from './EventMiniSparkline';
import { secondsToHumanReadable, formatNumber } from '../../utils/utils';
import {
  fetchRequest as fetchStreamEvents,
  clearState as clearStreamEvents,
} from '../../redux/actions/events/streamEvents';

const PAGE = 50;

const fmtWhen = (ms) => {
  if (!ms) return '—';
  try {
    return new Date(Number(ms)).toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch (_e) { return '—'; }
};

const StatBlock = ({ label, children }) => (
  <Box sx={{ minWidth: 96 }}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    {children}
  </Box>
);

const EventRow = ({ event }) => {
  const theme = useTheme();
  const scores = event.scores || {};
  const importance = Number(scores.importance);
  const polarity = Number(scores.polarity);
  const scheduled = Boolean(event.scheduled)
    && Number(event.occurred_at) > Date.now();
  const lateArrival = !scheduled && event.timestamp && event.occurred_at
    && (Number(event.timestamp) - Number(event.occurred_at)) > 6 * 3600 * 1000;
  return (
    <Stack
      direction="row" spacing={1.5} alignItems="flex-start"
      sx={{
        py: 1, px: 1.25, borderRadius: 1,
        '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.03) },
      }}
    >
      <Box sx={{ width: 118, flexShrink: 0 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
          {fmtWhen(event.occurred_at)}
        </Typography>
        {scheduled && (
          <Chip
            size="small" icon={<ScheduleIcon sx={{ fontSize: 11 }} />}
            label="scheduled"
            sx={{
              mt: 0.25, height: 17, fontSize: 9.5, fontWeight: 700,
              color: theme.palette.warning.main,
              bgcolor: alpha(theme.palette.warning.main, 0.12),
              '& .MuiChip-icon': { color: theme.palette.warning.main },
            }}
          />
        )}
        {lateArrival && (
          <Tooltip title={`Recorded ${fmtWhen(event.timestamp)} — well after it happened. The engine revised history instead of treating it as breaking news.`}>
            <Typography variant="caption" color="text.disabled" display="block">
              recorded late
            </Typography>
          </Tooltip>
        )}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.35 }}>
          {event.name || event.text || '—'}
        </Typography>
        {event.text && event.text !== event.name && (
          <Typography
            variant="caption" color="text.secondary"
            sx={{
              display: '-webkit-box', WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical', overflow: 'hidden',
            }}
          >
            {event.text}
          </Typography>
        )}
      </Box>
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0, pt: 0.25 }}>
        {Number.isFinite(importance) && (
          <Tooltip title="LLM-scored importance">
            <Chip
              size="small" label={`${Math.round(importance * 100)}%`}
              sx={{ height: 18, fontSize: 10, fontWeight: 700 }}
            />
          </Tooltip>
        )}
        {Number.isFinite(polarity) && polarity !== 0 && (
          polarity > 0
            ? <TrendingUpIcon sx={{ fontSize: 15, color: theme.palette.success.main }} />
            : <TrendingDownIcon sx={{ fontSize: 15, color: theme.palette.error.main }} />
        )}
        {event.value != null && (
          <Tooltip title="Numeric payload — feeds the event's weight in models">
            <Chip
              size="small" variant="outlined" label={formatNumber(event.value)}
              sx={{ height: 18, fontSize: 10, fontVariantNumeric: 'tabular-nums' }}
            />
          </Tooltip>
        )}
      </Stack>
    </Stack>
  );
};

const EventStreamDetail = ({ signal }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { response, isLoading, error } = useSelector(
    (s) => s.event_stream_events || {},
  );
  // Accumulate pages locally (the slice holds one page at a time).
  const [events, setEvents] = useState([]);
  const isMkt = Boolean(signal?.marketplace);
  const stream = signal?.stream || signal?.key;

  useEffect(() => {
    setEvents([]);
    if (!isMkt && stream) {
      dispatch(fetchStreamEvents({ stream, limit: PAGE }));
    }
    return () => { dispatch(clearStreamEvents()); };
  }, [dispatch, stream, isMkt]);

  useEffect(() => {
    if (!response?.events) return;
    setEvents((prev) => {
      const seen = new Set(prev.map((e) => e.event_id));
      return [...prev, ...response.events.filter((e) => !seen.has(e.event_id))];
    });
  }, [response]);

  const edges = signal?.edges;
  const scheduledCount = signal?.scheduled_count || 0;
  const oldestShown = useMemo(() => (
    events.length ? Math.min(...events.map((e) => Number(e.occurred_at) || Infinity)) : null
  ), [events]);

  const list = isMkt
    ? [...(signal?.points || [])].reverse().map((p) => ({
      event_id: `${p.occurred_at}-${p.rank}`,
      name: p.name, occurred_at: p.occurred_at, timestamp: p.timestamp,
      scores: { importance: p.importance, polarity: p.polarity }, value: p.value,
      scheduled: Number(p.occurred_at) > Date.now(),
    }))
    : events;

  return (
    <Box>
      {/* Stats strip: the stream at a glance, discovery verdict included. */}
      <Stack
        direction="row" spacing={3} flexWrap="wrap" rowGap={1.5}
        sx={{ mb: 1.5, px: 0.5 }}
      >
        <StatBlock label="Events">
          <Typography variant="subtitle2">{signal?.signal_count || list.length}</Typography>
        </StatBlock>
        {scheduledCount > 0 && (
          <StatBlock label="Scheduled">
            <Tooltip title={signal?.next_scheduled_at
              ? `Next: ${fmtWhen(signal.next_scheduled_at)}` : ''}
            >
              <Typography variant="subtitle2" sx={{ color: theme.palette.warning.main }}>
                {scheduledCount} upcoming
              </Typography>
            </Tooltip>
          </StatBlock>
        )}
        <StatBlock label="Forecast impact">
          {edges?.goals_affected ? (
            <Tooltip title={`Validated edges — max lift ${formatNumber(edges.max_lift)}${edges.half_life_s ? ` · effect half-life ${secondsToHumanReadable(edges.half_life_s)}` : ''}${edges.weakening ? ` · ${edges.weakening} weakening` : ''}`}>
              <Chip
                size="small" icon={<BoltIcon sx={{ fontSize: 12 }} />}
                label={`drives ${edges.goals_affected} goal${edges.goals_affected === 1 ? '' : 's'}`}
                sx={{
                  height: 20, fontSize: 10.5, fontWeight: 600,
                  color: theme.palette.error.main,
                  bgcolor: alpha(theme.palette.error.main, 0.1),
                  '& .MuiChip-icon': { color: theme.palette.error.main },
                }}
              />
            </Tooltip>
          ) : (
            <Tooltip title="No validated edge yet — discovery keeps testing this stream against your goals as events accumulate.">
              <Typography variant="caption" color="text.disabled">
                no validated edges yet
              </Typography>
            </Tooltip>
          )}
        </StatBlock>
        {edges?.half_life_s ? (
          <StatBlock label="Effect half-life">
            <Typography variant="subtitle2">
              {secondsToHumanReadable(edges.half_life_s)}
            </Typography>
          </StatBlock>
        ) : null}
      </Stack>

      {(signal?.points || []).length > 1 && (
        <Box sx={{ mb: 1.5 }}>
          <EventMiniSparkline points={signal.points} height={72} />
        </Box>
      )}

      <Divider sx={{ mb: 0.5 }} />

      {error && <Alert severity="error" sx={{ my: 1 }}>{String(error)}</Alert>}
      {isLoading && list.length === 0 && (
        <Stack alignItems="center" sx={{ py: 3 }}><CircularProgress size={20} /></Stack>
      )}
      {!isLoading && list.length === 0 && !error && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2, px: 1 }}>
          No events recorded on this stream yet.
        </Typography>
      )}

      <Box sx={{ maxHeight: 420, overflowY: 'auto' }}>
        {list.map((e) => <EventRow key={e.event_id} event={e} />)}
      </Box>

      {!isMkt && response?.has_more && oldestShown && (
        <Stack alignItems="center" sx={{ mt: 1 }}>
          <CMButtonOutlined
            size="small" disabled={isLoading}
            sx={{ height: 30, fontSize: 12 }}
            onClick={() => dispatch(fetchStreamEvents({
              stream, limit: PAGE, before: oldestShown,
            }))}
          >
            {isLoading ? 'Loading…' : 'Load older events'}
          </CMButtonOutlined>
        </Stack>
      )}
    </Box>
  );
};

export default EventStreamDetail;
